import createApiService from "@lib/create-api-service";
import { removeTokenCookie } from "@lib/street-autocomplete/services/updateCookie";
import { ADDRESS_SEARCH } from "@utils/constants";

import { MAX_TRIES } from "../constants";
import { validateSuggestions } from "../models/suggestions";
import { TSuggestionsResponse, TSuggestions, TStreetSuggestionQuery } from "../types";
import { getAddressFilter } from "../utils/getAddressFilter";
import { getToken } from "./getToken";

export const getSuggestions = createApiService<TSuggestionsResponse>({
  method: "GET",
  baseURL: ADDRESS_SEARCH.ADDRESS_SEARCH_BASE_URL,
  url: "/autosuggest",
  isExternalApi: true,
});

const loadStreetSuggestions = async ({
  streetQuery,
  defaultLocale,
  country,
  count = 0,
}: TStreetSuggestionQuery): Promise<{ data: TSuggestions[]; error?: string | undefined }> => {
  try {
    if (!streetQuery || !streetQuery.length) {
      return { data: [], error: undefined };
    }

    const countryCode = getAddressFilter(country ?? defaultLocale);

    const params = new URLSearchParams();
    params.append("q", streetQuery);
    params.append("in", countryCode);
    params.append("at", ADDRESS_SEARCH.COORDINATES[defaultLocale]);
    params.append("types", "street,houseNumber");
    params.append("lang", "en-US");

    const token = await getToken(defaultLocale);

    const response = await getSuggestions({
      params,
      // eslint-disable-next-line @typescript-eslint/naming-convention
      headers: { Authorization: `Bearer ${token}` } as any,
    });

    if (!response.isSuccess) {
      if (response.statusCode === 401 && count < MAX_TRIES) {
        removeTokenCookie();
        const incrementedCount = count + 1;
        return await loadStreetSuggestions({ streetQuery, defaultLocale, country, count: incrementedCount });
      }
      return { data: [], error: response.errorMessage };
    }

    const { items } = validateSuggestions(response.data);

    return { data: items.map(({ id, title }) => ({ id, title })) };
  } catch (err) {
    return { data: [], error: undefined };
  }
};

export default loadStreetSuggestions;
